/**
 * Legacy WordPress URL -> current URL, in ONE place.
 *
 * WHY THIS MODULE EXISTS
 * These maps used to live only inside `next.config.ts`'s `redirects()`, which meant a legacy URL
 * still embedded in CMS body copy resolved via a 301 rather than pointing at the real page. A
 * full link audit of all 408 published blog URLs found **380 such in-content links across 146
 * articles** -- nothing user-visibly broken, every one of them costing a redirect hop and
 * diluting the internal link equity that the target page should receive:
 *
 *     45x /features/ultimate-candidate-profile      -> /features/candidate-relationship-management
 *     45x /features/candidate-history               -> /features/candidate-relationship-management
 *     44x /features/advanced-grouping-and-tagging   -> /features/candidate-relationship-management
 *     33x /features/interview-scheduling            -> /features/video-interviews
 *     ... 30 more targets
 *
 * `next.config.ts` builds its redirect rules from these maps (so old inbound links and any URL we
 * do not control keep working), and `rewriteLegacyLinks` below rewrites the same paths inside CMS
 * bodies at render time (so our own content stops paying the hop). One source of truth: adding a
 * legacy slug here fixes both at once.
 *
 * NOTE: `next.config.ts` imports this with a RELATIVE path. The `@/` alias is a tsconfig `paths`
 * entry resolved by the bundler, and the config file is evaluated before that applies.
 */

/**
 * Old flat feature slugs (WP) -> new feature slugs. Confident 1:1 maps where the topic clearly
 * matches; an empty value means "no close equivalent" and falls back to the /features hub.
 */
export const FEATURE_SLUG_MAP: Record<string, string> = {
  "advanced-integrations": "api-integrations",
  "ai-based-candidate-sourcing": "candidate-sourcing",
  "sourcing-extension": "candidate-sourcing",
  "adding-candidate-resumes-manually": "candidate-relationship-management",
  "advanced-grouping-and-tagging": "candidate-relationship-management",
  "candidate-history": "candidate-relationship-management",
  "direct-link-with-the-candidate": "candidate-relationship-management",
  "ultimate-candidate-profile": "candidate-relationship-management",
  "creating-and-managing-talent-pool": "candidate-relationship-management",
  "advanced-candidate-scorecards": "ai-candidate-evaluation",
  "multipurpose-online-forms": "candidate-survey",
  "multi-level-user-roles": "security-management",
  "multiple-workflows": "workflow-automation",
  "create-a-job-requisition-within-seconds": "recruitment-automation",
  "global-job-boards": "job-distribution",
  "publish-vacancies-on-social-accounts": "job-distribution",
  "corporate-social-media-posting": "job-distribution",
  "branded-career-page": "recruitment-marketing",
  "connect-your-corporate-career-site": "recruitment-marketing",
  "interview-scheduling": "video-interviews",
  // Ambiguous -- no close 1:1 match; send to the features hub.
  "built-in-calendar-and-mailbox": "",
  "easy-to-use-interface": "",
};

/**
 * Blog posts: one still exists under a new silo; the rest are gone -> send to their (still-live)
 * silo hub so the link stays topically relevant.
 */
export const BLOG_PATH_MAP: Record<string, string> = {
  "/blog/candidate-relationship-management/optimizing-your-hiring-process-key-roles-in-a-structured-hiring-team-2":
    "/blog/the-ultimate-guide-to-recruitment-automation/optimizing-your-hiring-process-key-roles-in-a-structured-hiring-team",
  "/blog/candidate-relationship-management/top-10-must-implement-candidate-experience-surveys-for-2023":
    "/blog/candidate-relationship-management",
  "/blog/job-distribution-with-hirebee/what-is-a-job-distribution-how-hirebee-made-it-smarter":
    "/blog/job-distribution-with-hirebee",
  "/blog/the-ultimate-guide-to-candidate-survey/candidate-experience-surveys-what-data-matters-most":
    "/blog/the-ultimate-guide-to-candidate-survey",
  "/blog/the-ultimate-guide-to-candidate-survey/the-role-of-surveys-in-candidate-experience-creating-a-positive-recruitment-journey":
    "/blog/the-ultimate-guide-to-candidate-survey",
  "/blog/the-ultimate-guide-to-recruitment-automation/hr-recruitment-process-automation-key-benefits-and-advantages":
    "/blog/the-ultimate-guide-to-recruitment-automation",
  "/blog/the-ultimate-guide-to-recruitment-automation/the-ins-and-outs-of-full-cycle-recruiting-process-a-comprehensive-guide-to-finding-the-best-talent":
    "/blog/the-ultimate-guide-to-recruitment-automation",
  // Mis-typed/mis-cited variant of a live post; see the note in next.config.ts.
  "/blog/how-to-conduct-hr-compliance-risk-assessment-effectively":
    "/blog/how-to-conduct-hr-compliance-risk-assessment-efficiently",
};

/** Old WP path prefix -> current section. `:path*`-style prefix moves, not exact paths. */
export const PREFIX_MAP: Array<{ from: string; to: string; keepPath: boolean }> = [
  // Old customer-story showcase path -> current route (slugs are unchanged).
  { from: "/customer-story-showc", to: "/customer-stories", keepPath: true },
  // Old WP HR-tools taxonomy. These briefly pointed at per-category routes; categories are now a
  // client-side filter on the hub, not routes, so the whole taxonomy collapses to the library
  // hub -- which lists every category as a tab, so nothing is lost.
  { from: "/hr-tool-categories", to: "/hr-tool-and-template", keepPath: false },
];

/**
 * Resolve one legacy same-site PATH (no host, no query, no fragment) to its current path.
 * Returns null when the path is not legacy and should be left exactly as written.
 *
 * Exact matches only. A near-miss is left alone rather than guessed at -- a wrong rewrite in body
 * copy is worse than a redirect hop, because nothing downstream corrects it.
 */
export function resolveLegacyPath(path: string): string | null {
  if (!path.startsWith("/")) return null;

  const blog = BLOG_PATH_MAP[path];
  if (blog) return blog;

  const feature = path.match(/^\/(?:features|features-details)\/([^/]+)$/);
  if (feature) {
    const target = FEATURE_SLUG_MAP[feature[1]];
    // `undefined` = not a legacy slug (leave it). `""` = known-legacy but no 1:1 target.
    if (target === undefined) return null;
    return target ? `/features/${target}` : "/features";
  }

  for (const { from, to, keepPath } of PREFIX_MAP) {
    if (path === from || path.startsWith(`${from}/`)) {
      return keepPath ? to + path.slice(from.length) : to;
    }
  }

  return null;
}
